import React, { useState, useRef, useEffect, useMemo } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  Label,
  Row,
  Col,
  Spinner,
  FormGroup,
  FormFeedback,
} from "reactstrap";
import CommonButton from "./CommonButton";
import ViewSkeleton from "./ViewSkeleton";
import { useForm, Controller, useWatch } from "react-hook-form";
import CommonForm, { FormFieldConfig } from "./CommonForm";
import { validations } from "../../validations/FormValidations";
import styles from "../../modules/Clinical/CheckinCheckout/css/checkincheckout.module.scss";

/* ─────────────────────────────────────────────────────────────
   TYPES
 ───────────────────────────────────────────────────────────── */
export interface CheckInForm {
  checkinDate: string;
  checkinTime: string;
  allocatedlockerno?: string;
  verificationpin: string;
  qa_json: { questionid: number; answeroptionid: number }[];
  [key: string]: any;
}

export interface CheckInSubject {
  id: number;
  subjectNo: string;
  subjectName: string;
  initials: string;
}

interface CheckInModalProps {
  isOpen: boolean;
  subject: CheckInSubject | null;
  onConfirm: (form: CheckInForm) => void;
  onClose: () => void;
  isCheckinLoading: boolean;
  isChecklistLoading?: boolean;
  questionlist: any[];
  checkinDate?: string | null;
}

/* ─────────────────────────────────────────────────────────────
   COMPONENT
 ───────────────────────────────────────────────────────────── */
const CheckInModal: React.FC<CheckInModalProps> = ({
  isOpen,
  subject,
  onConfirm,
  onClose,
  isCheckinLoading,
  isChecklistLoading = false,
  questionlist,
  checkinDate,
}) => {
  const {
    control,
    handleSubmit,
    setValue,
    reset,
    formState: { errors },
  } = useForm<CheckInForm>({
    defaultValues: {
      checkinDate:     "",
      checkinTime:     "",
      allocatedlockerno: "",
      verificationpin: "",
      qa_json:         [],
    },
  });

  const pinRefs = [
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
  ];

  const [pinDigits, setPinDigits]         = useState(["", "", "", ""]);
  const [checkAnswers, setCheckAnswers]   = useState<Record<string, boolean>>({});

  // Reset on open 
  useEffect(() => {
    if (isOpen) {
      const nowTime = new Date().toTimeString().slice(0, 5);
      const initialValues: any = {
        checkinDate: checkinDate ? checkinDate.split("T")[0] : "",
        checkinTime: nowTime,
        allocatedlockerno: "",
        verificationpin: "",
      };
      reset(initialValues);
      setPinDigits(["", "", "", ""]);
    }
  }, [isOpen, reset, checkinDate]);

  if (!isOpen || !subject) return null;

  const fields: FormFieldConfig[] = [
    {
      name: "checkinDate",
      label: "Check-In Date",
      type: "date",
      required: true,
      placeholder: "dd-mm-yyyy",
      disabled: true,
      rules: validations.checkin.checkinDate,
    },
    {
      name: "checkinTime",
      label: "Check-In Time",
      type: "time",
      required: true,
      placeholder: "00:00",
      rules: validations.checkin.checkinTime,
    },
    {
      name: "allocatedlockerno",
      label: "Allocated Locker No",
      type: "text",
      required: false,
      placeholder: "Enter locker no",
    },
  ];

  const handlePinChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return;
    const updated = [...pinDigits];
    updated[index] = value.slice(-1);
    setPinDigits(updated);
    setValue("verificationpin", updated.join(""), { shouldValidate: true });
    if (value && index < 3) pinRefs[index + 1].current?.focus();
  };

  const handlePinKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace" && !pinDigits[index] && index > 0) {
      pinRefs[index - 1].current?.focus();
    }
  };

  const onSubmit = (data: any) => {
    onConfirm({ ...data });
  };

  return (
    <Modal isOpen={isOpen} toggle={onClose} size="lg" centered backdrop="static" className="common_modal">
      <ModalHeader toggle={onClose} className="p-3">
        Subject Check-In ({subject.subjectNo})
      </ModalHeader>

      <ModalBody className="px-4 pb-4">
        <CommonForm fields={fields} control={control} errors={errors} columns={3} setValue={setValue} />

        {/* Verification PIN */}
        <Row className="mt-3">
          <Col md={6}>
            <Label className={`fw-semibold mb-2 ${styles.required}`} htmlFor="verificationpin">
              Verification PIN
            </Label>
            <div className={styles.digitInputGroup}>
              {pinDigits.map((digit, i) => (
                <Input
                  key={i}
                  innerRef={pinRefs[i]}
                  type="password"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handlePinChange(i, e.target.value)}
                  onKeyDown={(e) => handlePinKeyDown(i, e)}
                  autoComplete="new-password"
                  className={`${styles.digitInput} ${errors.verificationpin ? styles.isInvalid : ""}`}
                />
              ))}
            </div>
            {errors.verificationpin && (
              <div className="text-danger mt-1 fs-12">{errors.verificationpin.message}</div>
            )}
            <Controller
              name="verificationpin"
              control={control}
              rules={validations.checkin.verificationpin}
              render={({ field }) => <input type="hidden" {...field} />}
            />
          </Col>
        </Row>
      </ModalBody>

      <ModalFooter className="bg-white border-top">
        <CommonButton color="dark" outline onClick={onClose} className="px-4">Cancel</CommonButton>
        <CommonButton color="primary" outline onClick={handleSubmit(onSubmit)} disabled={isCheckinLoading} className="px-4">
          {isCheckinLoading && <Spinner size="sm" className="me-2" />}
          Save
        </CommonButton>
      </ModalFooter>
    </Modal>
  );
};

export default CheckInModal;